import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Wallet, Pencil, Loader2, Play, CheckCircle2, Banknote, Download, Plus, Trash2 } from "lucide-react";

const ngn = (n, cur = "NGN") => (n == null ? "—" : `${cur === "NGN" ? "₦" : cur + " "}${Number(n).toLocaleString()}`);

async function downloadPayslip(slipId, period) {
  try {
    const { data } = await api.get(`/payroll/payslips/${slipId}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `payslip-${period}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch { toast.error("Could not download payslip."); }
}

function ManagerPayroll() {
  const [profiles, setProfiles] = useState([]);
  const [runs, setRuns] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ base_salary: "", allowances: "", deductions: "", bank_name: "", account_number: "" });
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [dueDate, setDueDate] = useState("");
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [expenses, setExpenses] = useState({ items: [], totals: {} });
  const [expForm, setExpForm] = useState({ category: "General", description: "", amount: "", currency: "NGN", date: "" });
  const [spend, setSpend] = useState({ months: [] });

  const load = async () => {
    const [p, r, e, s] = await Promise.all([api.get("/payroll/profiles"), api.get("/payroll/runs"), api.get("/payroll/expenses"), api.get("/payroll/spend-summary")]);
    setProfiles(p.data); setRuns(r.data); setExpenses(e.data); setSpend(s.data);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEdit(p);
    setForm({
      base_salary: p.base_salary ?? "", allowances: p.allowances ?? 0, deductions: p.deductions ?? 0,
      bank_name: p.bank_name ?? "", account_number: p.account_number ?? "",
    });
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/payroll/profiles/${edit.user_id}`, {
        base_salary: Number(form.base_salary) || 0, allowances: Number(form.allowances) || 0,
        deductions: Number(form.deductions) || 0, bank_name: form.bank_name || null, account_number: form.account_number || null,
      });
      toast.success("Salary saved.");
      setEdit(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const payableProfiles = profiles.filter((p) => (p.base_salary || 0) > 0);
  const chosen = selected.length ? payableProfiles.filter((p) => selected.includes(p.user_id)) : payableProfiles;
  const selectedTotal = chosen.reduce((s, p) => s + (p.net || 0), 0);
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const runPayroll = async () => {
    setBusy(true);
    try {
      await api.post("/payroll/runs", { period, user_ids: selected.length ? selected : null, due_date: dueDate || null });
      toast.success(`Payroll run for ${period} created.`);
      setSelected([]); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addExpense = async () => {
    try { await api.post("/payroll/expenses", { ...expForm, amount: Number(expForm.amount) || 0 }); toast.success("Expense added"); setExpForm({ category: "General", description: "", amount: "", currency: "NGN", date: "" }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delExpense = async (id) => { await api.delete(`/payroll/expenses/${id}`); load(); };
  const view = async (r) => { const { data } = await api.get(`/payroll/runs/${r.id}`); setDetail(data); };
  const pay = async (r) => {
    await api.post(`/payroll/runs/${r.id}/pay`);
    toast.success(`${r.period} payroll marked as paid.`);
    setDetail(null); load();
  };

  return (
    <Tabs defaultValue="salaries">
      <TabsList className="bg-[#121816] border border-emerald-500/15 w-full max-w-full overflow-x-auto justify-start">
        <TabsTrigger value="salaries" data-testid="tab-salaries">Employee salaries</TabsTrigger>
        <TabsTrigger value="runs" data-testid="tab-runs">Payroll runs</TabsTrigger>
        <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
        <TabsTrigger value="spend" data-testid="tab-spend">Spend</TabsTrigger>
      </TabsList>

      <TabsContent value="salaries" className="mt-4">
        <div className="card-emerald overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="salary-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-white/10">
                  <th className="py-3 px-3">Employee</th><th className="py-3 px-3">Base</th>
                  <th className="py-3 px-3">Allowances</th><th className="py-3 px-3">Deductions</th>
                  <th className="py-3 px-3">Net</th><th className="py-3 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.user_id} className="border-b border-white/5" data-testid="salary-row">
                    <td className="py-3 px-3"><div className="font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.email}</div></td>
                    <td className="py-3 px-3">{ngn(p.base_salary, p.currency)}</td>
                    <td className="py-3 px-3">{ngn(p.allowances, p.currency)}</td>
                    <td className="py-3 px-3">{ngn(p.deductions, p.currency)}</td>
                    <td className="py-3 px-3 font-semibold text-emerald-300">{ngn(p.net, p.currency)}</td>
                    <td className="py-3 px-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid="edit-salary-btn"><Pencil className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="runs" className="mt-4 space-y-4">
        <div className="card-emerald p-5 space-y-4">
          <div className="flex flex-col sm:flex-row items-end gap-3">
            <div><Label>Pay period</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="run-period-input" /></div>
            <div><Label>Pay due date (optional)</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="run-due-input" /></div>
            <Button onClick={runPayroll} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="run-payroll-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1" /> Run payroll</>}</Button>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2"><Label>Employees to pay</Label><button className="text-xs text-emerald-400" onClick={() => setSelected(selected.length ? [] : payableProfiles.map((p) => p.user_id))} data-testid="toggle-all-employees">{selected.length ? "Clear selection (pay all)" : "Select specific"}</button></div>
            <div className="max-h-48 overflow-y-auto border border-emerald-500/15 rounded-lg p-1 space-y-0.5" data-testid="employee-select-list">
              {payableProfiles.length === 0 && <p className="text-xs text-muted-foreground p-2">No salaries configured yet.</p>}
              {payableProfiles.map((p) => { const on = selected.length ? selected.includes(p.user_id) : true; return (
                <button key={p.user_id} type="button" onClick={() => toggleSel(p.user_id)} data-testid={`pay-emp-${p.user_id}`} className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-left text-sm ${on ? "bg-emerald-500/10" : "hover:bg-white/5"}`}>
                  <span className="flex items-center gap-2"><span className={`w-4 h-4 rounded border grid place-items-center text-[10px] ${on ? "bg-emerald-500 border-emerald-500 text-[#062217]" : "border-white/30"}`}>{on ? "✓" : ""}</span>{p.name}</span>
                  <span className="text-emerald-300">{ngn(p.net, p.currency)}</span>
                </button>); })}
            </div>
            <p className="text-sm mt-2 text-right">Total to pay: <span className="font-semibold text-emerald-300" data-testid="selected-total">{ngn(selectedTotal, chosen[0]?.currency || "NGN")}</span> · {chosen.length} employee(s)</p>
          </div>
        </div>
        {runs.length === 0 ? (
          <div className="card-emerald p-10 text-center text-muted-foreground">No payroll runs yet.</div>
        ) : runs.map((r) => (
          <div key={r.id} className="card-emerald p-5 flex items-center justify-between" data-testid="run-row">
            <div>
              <p className="font-semibold">{r.period}</p>
              <p className="text-xs text-muted-foreground">{r.count} employees · total {ngn(r.total_net, r.currency)}{r.due_date ? ` · due ${r.due_date}` : ""}</p>
              {r.status !== "paid" && r.due_date && (() => { const days = Math.ceil((new Date(r.due_date) - new Date()) / 86400000); return days >= 0 && days <= 7 ? <p className="text-xs text-amber-300 mt-0.5" data-testid="due-reminder">⏰ Salaries due in {days} day(s)</p> : null; })()}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={r.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}>{r.status}</Badge>
              <Button size="sm" variant="outline" className="border-emerald-500/30" onClick={() => view(r)} data-testid="view-run-btn">View</Button>
              {r.status !== "paid" && <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-[#062217]" onClick={() => pay(r)} data-testid="pay-run-btn"><Banknote className="w-4 h-4 mr-1" /> Mark paid</Button>}
            </div>
          </div>
        ))}
      </TabsContent>

      <TabsContent value="expenses" className="mt-4 space-y-4">
        <div className="card-emerald p-5 grid sm:grid-cols-5 gap-2 items-end" data-testid="expense-form">
          <div><Label>Category</Label><Input value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="exp-category" /></div>
          <div className="sm:col-span-2"><Label>Description</Label><Input value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="exp-desc" /></div>
          <div><Label>Amount</Label><Input type="number" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="exp-amount" /></div>
          <Button onClick={addExpense} disabled={!expForm.amount} className="bg-emerald-500 text-[#062217] font-semibold" data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
        <div className="flex gap-3 flex-wrap">{Object.entries(expenses.totals || {}).map(([c, v]) => <Badge key={c} className="bg-emerald-500/15 text-emerald-300" data-testid={`exp-total-${c}`}>Total {c}: {Number(v).toLocaleString()}</Badge>)}</div>
        <div className="card-emerald overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-white/10"><th className="py-3 px-3">Date</th><th className="py-3 px-3">Category</th><th className="py-3 px-3">Description</th><th className="py-3 px-3">Amount</th><th></th></tr></thead>
          <tbody>
            {(expenses.items || []).length === 0 ? <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No expenses recorded.</td></tr> : expenses.items.map((e) => (
              <tr key={e.id} className="border-b border-white/5" data-testid="expense-row"><td className="py-2.5 px-3">{e.date}</td><td className="py-2.5 px-3">{e.category}</td><td className="py-2.5 px-3">{e.description}</td><td className="py-2.5 px-3 font-semibold">{e.currency} {Number(e.amount).toLocaleString()}</td><td className="py-2.5 px-3 text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => delExpense(e.id)} data-testid={`del-exp-${e.id}`}><Trash2 className="w-4 h-4" /></Button></td></tr>
            ))}
          </tbody>
        </table></div></div>
      </TabsContent>

      <TabsContent value="spend" className="mt-4 space-y-4">
        <p className="text-sm text-muted-foreground">Monthly payroll + expenses combined — your total spend at a glance.</p>
        <div className="card-emerald overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-white/10"><th className="py-3 px-3">Month</th><th className="py-3 px-3">Payroll</th><th className="py-3 px-3">Expenses</th><th className="py-3 px-3">Total spend</th></tr></thead>
          <tbody>
            {(spend.months || []).length === 0 ? <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">No payroll or expenses yet.</td></tr> : spend.months.map((m) => (
              <tr key={m.month} className="border-b border-white/5" data-testid={`spend-${m.month}`}><td className="py-2.5 px-3 font-medium">{m.month}</td><td className="py-2.5 px-3">{m.currency} {Number(m.payroll).toLocaleString()}</td><td className="py-2.5 px-3">{m.currency} {Number(m.expenses).toLocaleString()}</td><td className="py-2.5 px-3 font-semibold text-emerald-300">{m.currency} {Number(m.total).toLocaleString()}</td></tr>
            ))}
          </tbody>
        </table></div></div>
      </TabsContent>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#121816] border-emerald-500/20">
          <DialogHeader><DialogTitle>Salary — {edit?.name}</DialogTitle>
            <DialogDescription>Set base pay, allowances and deductions. Net pay is calculated automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Base salary (₦)</Label><Input type="number" value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="salary-base-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Allowances (₦)</Label><Input type="number" value={form.allowances} onChange={(e) => setForm({ ...form, allowances: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="salary-allow-input" /></div>
              <div><Label>Deductions (₦)</Label><Input type="number" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="salary-deduct-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bank</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="salary-bank-input" /></div>
              <div><Label>Account no.</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="salary-acct-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="salary-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save salary"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#121816] border-emerald-500/20 max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Payslips — {detail?.period}</DialogTitle>
            <DialogDescription>Review each employee's net pay for this period, then mark the run as paid.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {detail?.payslips?.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <span className="text-sm">{s.name}</span>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-emerald-300">{ngn(s.net, s.currency)}</span>
                  <Badge className={s.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}>{s.status}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => downloadPayslip(s.id, s.period)} data-testid="download-payslip-btn"><Download className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
          {detail?.status !== "paid" && <DialogFooter><Button onClick={() => pay(detail)} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="detail-pay-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Mark all paid</Button></DialogFooter>}
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}

function EmployeePayroll() {
  const [profile, setProfile] = useState(null);
  const [slips, setSlips] = useState([]);
  useEffect(() => {
    (async () => {
      const [p, s] = await Promise.all([api.get("/payroll/me/profile"), api.get("/payroll/me/payslips")]);
      setProfile(p.data); setSlips(s.data);
    })();
  }, []);
  const cur = profile?.currency || "NGN";
  const net = profile ? (profile.base_salary || 0) + (profile.allowances || 0) - (profile.deductions || 0) : null;
  return (
    <div className="space-y-6">
      <div className="card-emerald p-6" data-testid="my-salary-card">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Your monthly net pay</p>
        <p className="mt-2 font-display text-4xl font-extrabold text-emerald-400">{profile ? ngn(net, cur) : "Not set yet"}</p>
        {profile && <p className="text-sm text-muted-foreground mt-2">Base {ngn(profile.base_salary, cur)} · Allowances {ngn(profile.allowances, cur)} · Deductions {ngn(profile.deductions, cur)}</p>}
      </div>
      <div className="card-emerald overflow-hidden">
        <div className="px-5 pt-5 font-display font-semibold">Payslip history</div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm" data-testid="my-payslips-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-white/10">
                <th className="py-3 px-5">Period</th><th className="py-3 px-3">Net</th><th className="py-3 px-3">Status</th><th className="py-3 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {slips.length === 0 ? (
                <tr><td colSpan={4} className="py-10 text-center text-muted-foreground">No payslips yet.</td></tr>
              ) : slips.map((s) => (
                <tr key={s.id} className="border-b border-white/5">
                  <td className="py-3 px-5">{s.period}</td>
                  <td className="py-3 px-3 font-semibold">{ngn(s.net, s.currency)}</td>
                  <td className="py-3 px-3"><Badge className={s.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}>{s.status}</Badge></td>
                  <td className="py-3 px-3 text-right"><Button size="sm" variant="ghost" aria-label="Download payslip PDF" title="Download PDF" onClick={() => downloadPayslip(s.id, s.period)} data-testid="download-payslip-btn"><Download className="w-4 h-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Payroll() {
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Wallet className="w-5 h-5 text-emerald-400" />
        <h1 className="font-display text-2xl font-bold">Payroll</h1>
      </div>
      {(user?.role === "super_admin" || (user?.role === "manager" && (user?.permissions || []).includes("view_payroll"))) ? <ManagerPayroll /> : <EmployeePayroll />}
    </div>
  );
}
